import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BackButton from "../../components/button/BackButton";
import columns from "../../columns/files/bookKeepingFile";
import SkeletonTable from "../../components/table/SkeletonTable";
import toast from "react-hot-toast";
import {
  GetBookKeepingApi,
  UpdateBookKeepingStatusApi,
} from "../../api/request/service";
import ViewBookKeepingModal from "./components/ViewBookKeepingModal";
import View from "../../assets/View";
import moment from "moment";
import { Button, Form, Input, Select, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import ModalComponent from "../../components/modals/ModalComponent";
import LoadableButton from "../../components/button/LoadableButton";
import TextArea from "antd/es/input/TextArea";

const BookKeepingFile = () => {
  const { state } = useLocation();
  const { id, fileTypeId } = useParams();
  const [completeStatusForm] = Form?.useForm();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBookOpen, setIsBookOpen] = useState(false);
  const [isCompleteStatus, setIsCompleteStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [selectValues, setSelectValues] = useState({});
  const [selectedViewData, setSelectedViewData] = useState(null);
  const [isRejectedStatus, setIsRejectedStatus] = useState(false);
  const [isCompleteDoc, setIsCompleteDoc] = useState(false);
  const [isFileError, setIsFileError] = useState(false);
  const [isReturnFileOpen, setIsReturnFileOpen] = useState(false);

  const getBookKeeping = async () => {
    try {
      setIsLoading(true);
      const params = { file_id: id };
      const { data } = await GetBookKeepingApi(params);
      const statusMap = {};
      data?.data?.forEach((item) => {
        statusMap[item.id] = item.status;
      });
      setSelectValues(statusMap);
      setData(
        data?.data?.map((item) => {
          return {
            ...item,
            submittedDate: item?.submitted_date
              ? moment(item?.submitted_date).format("lll")
              : "--",
            completionDocument: item?.completion_document ? (
              <p
                className="text-blue-500 underline"
                onClick={() =>
                  window?.open(item?.completion_document, "_blank")
                }
              >
                View
              </p>
            ) : (
              "--"
            ),
            completionNotes: (
              <p className="w-[150px] truncate" title={item?.completion_notes}>
                {item?.completion_notes || "--"}
              </p>
            ),
            completedDate: item?.completed_date
              ? moment(item?.completed_date).format("lll")
              : "--",
            action: (
              <button
                onClick={() => {
                  setIsBookOpen(true);
                  setSelectedViewData(item);
                }}
              >
                <View />
              </button>
            ),
            status:
              item?.status === "pending" ? (
                <Select
                  value={selectValues[item?.id] || "pending"}
                  options={[
                    { label: "Pending", value: "pending" },
                    { label: "Completed", value: "completed" },
                    { label: "Returned", value: "returned" },
                  ]}
                  onChange={(value) => handleStatusChange(item, value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: 120 }}
                />
              ) : item?.status === "returned" ? (
                <div
                  className="flex items-center gap-2"
                  onClick={(e) => {
                    e?.stopPropagation();
                    setIsReturnFileOpen(true);
                    setSelectedViewData(item);
                  }}
                >
                  <p
                    className={`font-bold uppercase ${
                      item?.status === "completed"
                        ? "text-green-500"
                        : "text-red-500"
                    }`}
                  >
                    {item?.status}
                  </p>
                  <img
                    src="/assets/icons/info.svg"
                    className="size-4 cursor-pointer"
                    title={item?.return_reason}
                  />
                </div>
              ) : (
                <p
                  className={`font-bold uppercase ${
                    item?.status === "completed"
                      ? "text-green-500"
                      : "text-red-500"
                  }`}
                >
                  {item?.status}
                </p>
              ),
          };
        })
      );

      setIsLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast?.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };

  const handleStatusChange = (row, newValue) => {
    setSelectValues((prev) => ({
      ...prev,
      [row?.id]: newValue,
    }));

    setSelectedStatus(newValue);
    setSelectedRow(row);
    if (newValue === "completed") {
      setIsCompleteStatus(true);
    } else if (newValue === "returned") {
      setIsRejectedStatus(true);
    }
  };

  const handleCancelStatusChange = () => {
    setSelectValues((prev) => ({
      ...prev,
      [selectedRow?.id]: selectedRow?.status,
    }));
    setIsCompleteStatus(false);
    setIsRejectedStatus(false);
    setSelectedRow(null);
    setSelectedStatus(null);
  };

  const handleConfirmStatusChange = async (values) => {
    try {
      setIsStatusLoading(true);

      const params = {
        id: selectedRow?.id,
        status: selectedStatus,
        ...(isCompleteStatus && {
          completion_notes: values?.completion_notes,
          completed_document: values?.completion_document?.[0]?.originFileObj,
        }),
        ...(isRejectedStatus && {
          return_reason: values?.return_reason,
        }),
      };

      const apiHeader = {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      };

      const { data } = await UpdateBookKeepingStatusApi(params, apiHeader);
      if (data?.status) {
        getBookKeeping();
        toast?.success(data?.message);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast?.error(error?.response?.data?.message);
    } finally {
      setIsStatusLoading(false);
    }
    setIsCompleteStatus(false);
    setIsRejectedStatus(false);
    setSelectedRow(null);
    setSelectedStatus(null);
  };

  const beforeUpload = (file) => {
    const isPDF = file?.type === "application/pdf";
    if (!isPDF) {
      setIsFileError(true);
      return true;
    } else {
      setIsFileError(false);
      return false;
    }
  };

  useEffect(() => {
    if (!isCompleteStatus) setIsCompleteDoc(false);
  }, [isCompleteStatus]);

  useEffect(() => {
    getBookKeeping();
  }, [id]);

  useEffect(() => {
    if (!isCompleteStatus || !isRejectedStatus)
      completeStatusForm?.resetFields();
  }, [isCompleteStatus, isRejectedStatus]);

  return (
    <>
      <div className="bg-white rounded-lg border border-1 mb-5">
        <div className="grid grid-cols-2 gap-3 p-4">
          <div className="space-y-1">
            <div>
              <p>
                <span className="font-semibold">Client Name : </span>{" "}
                {state?.client?.user?.name}
              </p>
            </div>
            <div>
              <p>
                <span className="font-semibold">Client Email : </span>{" "}
                {state?.client?.user?.email}
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <div>
              <p className="capitalize">
                <span className="font-semibold">File Name :</span>{" "}
                {state?.client?.service_name_slug}
              </p>
            </div>
            <div>
              <p>
                <span className="font-semibold">File No.:</span>{" "}
                {state?.client?.file_number}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-1 mb-5 p-5">
        <div className="flex items-center gap-3 mb-5">
          <BackButton />
          <div className="flex items-center gap-3 justify-between w-full">
            <h2 className="title">
              {state?.task?.name || "Review File"}{" "}
              {data?.length ? `(${data?.length})` : ""}
            </h2>
            <button
              className="btn-primary !px-3"
              onClick={() => navigate(`/file/${fileTypeId}/${id}`)}
            >
              View File Details
            </button>
          </div>
        </div>
        <SkeletonTable columns={columns} data={data} isLoading={isLoading} />
      </div>

      <ViewBookKeepingModal
        data={data}
        isLoading={isLoading}
        isOpen={isBookOpen}
        setIsOpen={setIsBookOpen}
        selectedData={selectedViewData}
        userId={state?.client?.user_id}
      />

      <ModalComponent
        isOpen={isCompleteStatus}
        setIsOpen={setIsCompleteStatus}
        onCancel={handleCancelStatusChange}
        title="Add Completion Document"
      >
        <div className="flex flex-col gap-4 mt-4 items-center w-full">
          <Form
            form={completeStatusForm}
            layout="vertical"
            className="w-full"
            onFinish={handleConfirmStatusChange}
          >
            <Form.Item
              className="capitalize"
              label="Completion Document"
              name="completion_document"
              valuePropName="fileList"
              getValueFromEvent={(e) => (Array?.isArray(e) ? e : e?.fileList)}
            >
              <Upload
                multiple={false}
                maxCount={1}
                accept="application/pdf"
                beforeUpload={beforeUpload}
              >
                <Button icon={<UploadOutlined />}>Select File</Button>
              </Upload>
              {isFileError ? (
                <p className="text-red-500">
                  Only PDF file is allowed. Please upload a valid PDF document.
                </p>
              ) : null}
            </Form.Item>

            <Form.Item
              className="capitalize"
              label="Completion Notes"
              name="completion_notes"
            >
              <TextArea
                placeholder="Enter Completion Notes"
                className="w-full"
              />
            </Form.Item>

            <LoadableButton
              type="submit"
              lable="Submit"
              isLoading={isStatusLoading}
              loadingLable="Submitting..."
            />
          </Form>
        </div>
      </ModalComponent>

      <ModalComponent
        isOpen={isRejectedStatus}
        setIsOpen={setIsRejectedStatus}
        onCancel={handleCancelStatusChange}
        title="Reject File"
      >
        <Form
          form={completeStatusForm}
          layout="vertical"
          onFinish={handleConfirmStatusChange}
          className="w-full"
        >
          <Form.Item
            className="capitalize"
            label="Reason of Return File"
            name="return_reason"
            rules={[
              {
                required: true,
                message: "Please enter rejection reason",
              },
            ]}
          >
            <TextArea placeholder="Enter rejection reason" />
          </Form.Item>

          <LoadableButton
            type="submit"
            lable="Submit"
            isLoading={isStatusLoading}
            loadingLable="Submitting..."
          />
        </Form>
      </ModalComponent>

      <ModalComponent
        title="Return File Reason"
        isOpen={isReturnFileOpen}
        setIsOpen={setIsReturnFileOpen}
      >
        <p className="text-base text-center">
          {selectedViewData?.return_reason}
        </p>
      </ModalComponent>
    </>
  );
};

export default BookKeepingFile;
